import { Injectable, signal } from '@angular/core';
import { createAction, props, Store } from '@ngrx/store';
import { ConfigurationService } from '../configuration/configuration.service';
import { CookieService } from './cookie.service';
import { NavigationService } from './navigation.service';

export const defaultDeliveryCountry = 'AT';
export const validDeliveryCountries = ['AT', 'DE'];

export const deliveryCountryChanged = createAction(
    '[Delivery Country] Delivery country changed',
    props<{ newCountry: string; isOnExpressCheckout: boolean }>()
);

@Injectable({
    providedIn: 'root'
})
export class DeliveryCountryCookieService {
    countryFromAutologin = defaultDeliveryCountry; // defaults to 'AT'
    cookieName: string;

    private internalCountry$$ = signal('');
    private hideDeliveryCountryFeatureFlag = false;

    get country$$() {
        return this.internalCountry$$.asReadonly();
    }

    constructor(
        private cookieService: CookieService,
        private configService: ConfigurationService,
        private store: Store,
        private navigationService: NavigationService
    ) {
        // TODO: `country` query param overrides everything? waiting for story: https://shoepping.atlassian.net/browse/SHP-4022

        const apiHost = this.configService.get<string>('apiHost');
        const env = apiHost?.match(/api\.?(\w+)?\.?shoepping\.at/)?.[1];
        this.cookieName = `${!env ? '' : env + '.'}delivery_country`;

        this.hideDeliveryCountryFeatureFlag = !!configService.get<boolean>('hideDeliveryCountryFeatureFlag', true);
        if (this.hideDeliveryCountryFeatureFlag) {
            // overwrite possible already existing cookie to 'AT' if different delivery countries are disabled
            this.setCountry(defaultDeliveryCountry);
        }

        // initialize country signal with cookie value or default if not yet set (or not a valid and supported country code)
        // NOTE: we do not want to set the cookie yet with the fallback/default value as there is more logic in place after autologin and cart loading has happened
        const cookieValue = this.getCookie();
        const startingCountryValue = this.isCookieValueValid(cookieValue) ? cookieValue : defaultDeliveryCountry;
        this.internalCountry$$.set(startingCountryValue);
    }

    hasCookie() {
        return this.isCookieValueValid(this.getCookie());
    }

    setCountry(country: string) {
        if (this.hideDeliveryCountryFeatureFlag) {
            // disable different delivery country option
            country = defaultDeliveryCountry;
        }

        if (!this.isCookieValueValid(country)) {
            // allow only valid country values
            country = defaultDeliveryCountry;
        }

        this.setCookie(country);

        this.internalCountry$$.set(country);
    }

    changeCountry(newCountry: string, isOnExpressCheckout = false) {
        if (this.hideDeliveryCountryFeatureFlag) {
            // disable different delivery country option
            newCountry = defaultDeliveryCountry;
        }

        if (this.country$$() !== newCountry) {
            this.setCountry(newCountry);
            this.store.dispatch(deliveryCountryChanged({ newCountry, isOnExpressCheckout }));
            this.navigationService.refreshCurrentRoute();
        }
    }

    setCountryWithAutologinCountry() {
        this.setCountry(this.countryFromAutologin);
    }

    private setCookie(country) {
        this.cookieService.set(this.cookieName, country, {
            domain: 'shoepping.at',
            path: '/',
            expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365 * 10) // expire in 10 years
        });
    }

    private getCookie() {
        return this.cookieService.get(this.cookieName);
    }

    private isCookieValueValid(cookieValue: string) {
        return validDeliveryCountries.indexOf(cookieValue) > -1;
    }
}
